import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';

import DoughnutChart from '../DoughnutChart';
import { InputTable } from '../../assets/styles/InputTable';
import { color } from '../../assets/styles/theme';
import useTodayDate from '../../hooks/useTodayDate';
import GetFetchStatus from '../../hooks/GetFetchStatus';
import { todayYYYYMMDD } from '../../utils/formatDate';
import { useSetSjcdWDelY } from '../../utils/useSetSjcd';
import IssueGuide from '../IssueGuide';
import SkeletonTable from '../SkeletonTable';
import { DayPicker } from './PeriodPicker';
import { userState } from '../../atoms';

// 출역 정보
export const AttendInfo = ({ name, theme }: any) => {
  const { sCd } = useRecoilValue(userState);
  const { t } = useTranslation();
  const [innerTab, setInnerTab] = useState<number>(0);
  const [attendCount, setAttendCount] = useState<any>({ todayCnt: 0, yesterdayCnt: 0, towDaysBeforeCnt: 0 });
  const [attendPercent, setAttendPercent] = useState<any>({ todayPercent: 0, yesterdayPercent: 0, towDaysBeforePercent: 0 });
  const { selectedDay, setSelectedDay } = useTodayDate(null);
  const todayYMD = todayYYYYMMDD();
  const [selectedDayString, setSelectedDayString] = useState(todayYMD);
  const [tableState, setTableState] = useState<any[] | null>(null);
  const { sjCdList, isError: isErrorSjCdList } = useSetSjcdWDelY();

  const getPastDates = () => {
    // 선택한 날짜로부터 오늘,어제,2일전 날짜 구하기
    const today = selectedDay.format('YYYY-MM-DD');
    const yesterday = selectedDay.subtract(1, 'day').format('YYYY-MM-DD');
    const twoDaysBefore = selectedDay.subtract(2, 'day').format('YYYY-MM-DD');

    return {
      today,
      yesterday,
      twoDaysBefore,
    };
  };

  const {
    data: dayAttendData,
    isError: isErrorDayAttendData,
    refetch: refetchDayAttend,
  } = GetFetchStatus({
    path: '/attend/day/summary',
    req: { aDate1: getPastDates().twoDaysBefore.replaceAll('-', ''), aDate2: getPastDates().today.replaceAll('-', '') },
    queryKey: selectedDay.format('DD/MM/YYYY'),
    enabledKey: sCd !== '00000',
  });

  useEffect(() => {
    if (dayAttendData && sjCdList) {
      const dates = getPastDates();
      const { sjCounts = [] } = dayAttendData;

      // 날짜별 합산으로 전체 카운트 계산
      const todayCnt = sjCounts.filter((item: any) => item.aDate === dates.today).reduce((s: number, item: any) => s + item.cnt, 0);
      const yesterdayCnt = sjCounts.filter((item: any) => item.aDate === dates.yesterday).reduce((s: number, item: any) => s + item.cnt, 0);
      const twoDaysBeforeCnt = sjCounts.filter((item: any) => item.aDate === dates.twoDaysBefore).reduce((s: number, item: any) => s + item.cnt, 0);
      const counts = { todayCnt, yesterdayCnt, twoDaysBeforeCnt };
      setAttendCount(counts);
      setAttendPercent(getPercents(counts));

      // sjCd별로 그룹핑하여 날짜별 카운트 집계
      const grouped = new Map<string, { sjCd: string; sjName: string; todayCnt: number; yesterdayCnt: number; twoDaysBeforeCnt: number }>();
      for (const item of sjCounts) {
        const findSjCdName = sjCdList.find((el: any) => el.sjCd === item.sjCd);
        const sjName = findSjCdName?.cdName ?? '';
        if (!grouped.has(item.sjCd)) {
          grouped.set(item.sjCd, { sjCd: item.sjCd, sjName, todayCnt: 0, yesterdayCnt: 0, twoDaysBeforeCnt: 0 });
        }
        const entry = grouped.get(item.sjCd)!;
        if (item.aDate === dates.today) entry.todayCnt += item.cnt;
        if (item.aDate === dates.yesterday) entry.yesterdayCnt += item.cnt;
        if (item.aDate === dates.twoDaysBefore) entry.twoDaysBeforeCnt += item.cnt;
      }
      setTableState(Array.from(grouped.values()));
    }
  }, [dayAttendData, sjCdList]);

  const getCounts = (dateInfo: any, attendInfo: []) => {
    const todayCnt = attendInfo.filter((item: any) => item.aDate === dateInfo.today).length;
    const yesterdayCnt = attendInfo.filter((item: any) => item.aDate === dateInfo.yesterday).length;
    const twoDaysBeforeCnt = attendInfo.filter((item: any) => item.aDate === dateInfo.twoDaysBefore).length;

    return {
      todayCnt,
      yesterdayCnt,
      twoDaysBeforeCnt,
    };
  };

  const getPercents = (countParam: any) => {
    const total = countParam.todayCnt + countParam.yesterdayCnt + countParam.twoDaysBeforeCnt;
    const result = {
      todayPercent: Math.round((countParam.todayCnt / total) * 100),
      yesterdayPercent: Math.round((countParam.yesterdayCnt / total) * 100),
      twoDaysBeforePercent: Math.round((countParam.twoDaysBeforeCnt / total) * 100),
    };
    return result;
  };

  const onClickDate = (dateParam: string) => {
    setSelectedDay(dayjs(dateParam));
    setSelectedDayString(dateParam.replaceAll('-', ''));
  };

  const onClickRefresh = () => {
    refetchDayAttend();
  };

  const getTodayCnt = () => {
    return tableState
      ?.map((el: any) => el.todayCnt)
      .reduce((prev, cur) => {
        return prev + cur;
      }, 0);
  };
  const getYesterdayCnt = () => {
    return tableState
      ?.map((el: any) => el.yesterdayCnt)
      .reduce((prev, cur) => {
        return prev + cur;
      }, 0);
  };
  const getTwoDaysBeforeCnt = () => {
    return tableState
      ?.map((el: any) => el.twoDaysBeforeCnt)
      .reduce((prev, cur) => {
        return prev + cur;
      }, 0);
  };

  const loading = !sjCdList || !tableState;
  if (isErrorDayAttendData || isErrorSjCdList) return <IssueGuide />;

  return (
    <div className='attend'>
      <div className='widget-header'>
        <div className='widget-title flex-between innerTab'>
          <div className='widget-title-innerTab'>
            <span>{t(name)}</span>
            <div className='innerTab flex-basic'>
              <div className={innerTab === 0 ? 'active' : undefined} role='presentation' onClick={() => setInnerTab(0)}>
                {t('출역')}
              </div>
              <div className={innerTab === 1 ? 'active' : undefined} role='presentation' onClick={() => setInnerTab(1)}>
                {t('협력업체')}
              </div>
            </div>
          </div>
          <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
            <span className='material-symbols-rounded'>refresh</span>
          </div>
        </div>
        {sjCdList && tableState && <DayPicker dayCount={7} selectedValue={selectedDayString} handleSelect={onClickDate} />}
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable />
        </div>
      ) : innerTab === 0 ? (
        <div className='widget-body chart'>
          <div className='doughnutChartWrapper'>
            <DoughnutChart
              showLegend={false}
              percentage={[attendPercent.todayPercent, attendPercent.yesterdayPercent, attendPercent.twoDaysBeforePercent]}
              label={[t('금일'), t('전일'), t('2일 전')]}
              centerText={false}
              colorSet={['#3E97FF', '#50CD89', '#F1416C']}
              // colorSet={['#4f6be8', '#344dad', '#031339']}
              borderStatus={false}
            />
          </div>
          <div className='content'>
            <dl>
              <dd className='chart-color blue' />
              <dt>{t('금일')}</dt>
              <span>
                <span className='value'>{attendCount.todayCnt}</span> {t('명')}
              </span>
            </dl>
            <dl>
              <dd className='chart-color green' />
              <dt>{t('전일')}</dt>
              <span>
                <span className='value'>{attendCount.yesterdayCnt}</span> {t('명')}
              </span>
            </dl>
            <dl>
              <dd className='chart-color red' />
              <dt>{t('2일 전')}</dt>
              <span>
                <span className='value'>{attendCount.twoDaysBeforeCnt}</span> {t('명')}
              </span>
            </dl>
          </div>
        </div>
      ) : (
        <div className='widget-body table'>
          <InputTable>
            <div className='thead'>
              <div className='tr'>
                <div className='trCol2p5 flex-basic tableStickyNo content-overflow padding-left'>{t('협력업체')}</div>
                <div className='trCol3 flex-center'>{t('금일')}</div>
                <div className='trCol3 flex-center'>{t('전일')}</div>
                <div className='trCol4 flex-center padding-right'>{t('2일 전')}</div>
              </div>
            </div>
            <div className='table'>
              <div className='tbody'>
                {tableState?.length > 0 ? (
                  tableState.map((el: any, i: number) => (
                    <div className='tr' key={`attendInfo_${i}`}>
                      <div className='trCol2p5 flex-basic tableStickyNo content-overflow padding-left'>{el.sjName}</div>
                      <div className='trCol3 flex-center shrink0'>
                        <span className={`pointValue ${el.todayCnt > 0 && el.todayCnt <= 10 ? 'under10' : ''} ${el.todayCnt > 10 ? 'over10' : ''}`}>{el.todayCnt}</span>
                      </div>
                      <div className='trCol3 flex-center shrink0'>
                        <span className={`pointValue ${el.yesterdayCnt > 0 && el.yesterdayCnt <= 10 ? 'under10' : ''} ${el.yesterdayCnt > 10 ? 'over10' : ''}`}>{el.yesterdayCnt}</span>
                      </div>
                      <div className='trCol4 flex-center shrink0 padding-right'>
                        <span className={`pointValue ${el.twoDaysBeforeCnt > 0 && el.twoDaysBeforeCnt <= 10 ? 'under10' : ''} ${el.twoDaysBeforeCnt > 10 ? 'over10' : ''}`}>{el.twoDaysBeforeCnt}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className='noData'>No data.</div>
                )}
              </div>
            </div>
            <div className='tfoot'>
              <div className='tr'>
                <div className='trCol2p5 flex-basic tableStickyNo content-overflow padding-left'>
                  {t('합계')}
                  <span className='subNumber'>{tableState.length}</span>
                </div>
                <div className='trCol3 flex-center shrink0'>
                  <span className={`pointValue ${getTodayCnt() > 0 && getTodayCnt() <= 10 ? 'under10' : ''} ${getTodayCnt() > 10 ? 'over10' : ''}`}>{getTodayCnt()}</span>
                </div>
                <div className='trCol3 flex-center shrink0'>
                  <span className={`pointValue ${getYesterdayCnt() > 0 && getYesterdayCnt() <= 10 ? 'under10' : ''} ${getYesterdayCnt() > 10 ? 'over10' : ''}`}>{getYesterdayCnt()}</span>
                </div>
                <div className='trCol4 flex-center shrink0 padding-right'>
                  <span className={`pointValue ${getTwoDaysBeforeCnt() > 0 && getTwoDaysBeforeCnt() <= 10 ? 'under10' : ''} ${getTwoDaysBeforeCnt() > 10 ? 'over10' : ''}`}>
                    {getTwoDaysBeforeCnt()}
                  </span>
                </div>
              </div>
            </div>
          </InputTable>
        </div>
      )}
    </div>
  );
};
